import * as React from 'react'
import { focusRing } from '@repo/studio-design-system/utilities'
import { Slot } from 'primitives/slot'
import * as TabsPrimitive from 'primitives/tabs'
import { tw } from 'utils/classnames'

export type SectionTabsVariant = 'regular' | 'subtle'
export type SectionTabsSize = 'large' | 'medium' | 'small'

export type SectionTabsRootProps = React.ComponentProps<typeof TabsPrimitive.Root>

export interface SectionTabsListProps extends Omit<React.ComponentProps<'div'>, 'ref'> {
  ref?: React.Ref<HTMLDivElement>
}

export interface SectionTabsTabProps extends Omit<React.ComponentProps<'button'>, 'ref' | 'value'> {
  /** Selects the `Panel` with the same value. Selection lives on `Root`, not on the tab. */
  value: string
  /** Visual treatment. Regular is filled; Subtle is ghost until hover. @default 'regular' */
  variant?: SectionTabsVariant
  /** Height scale from Figma: large 40px, medium 32px, small 28px. @default 'large' */
  size?: SectionTabsSize
  /** Optional leading icon. Omit to render a label-only tab. */
  icon?: React.ReactNode
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export type SectionTabsPanelProps = React.ComponentProps<typeof TabsPrimitive.Content>

/** Owns which tab is selected. Wrap the `List` and every `Panel`. */
const SectionTabsRoot = TabsPrimitive.Root

function SectionTabsList({ className, ref, ...props }: SectionTabsListProps) {
  return (
    <TabsPrimitive.List
      {...props}
      ref={ref}
      className={tw('inline-flex items-center gap-1', className)}
    />
  )
}

function SectionTabsTab({
  value,
  variant = 'regular',
  size = 'large',
  icon,
  asChild,
  ref,
  className,
  children,
  disabled,
  ...props
}: SectionTabsTabProps) {
  const Comp = asChild ? Slot : 'button'
  const buttonProps = asChild ? {} : { type: 'button' as const }

  return (
    <TabsPrimitive.Trigger asChild disabled={disabled} value={value}>
      <Comp
        {...buttonProps}
        {...props}
        ref={ref}
        className={tw(
          sectionTabsClasses.base,
          sectionTabsClasses.sizes[size],
          sectionTabsClasses.variants[variant],
          disabled && 'cursor-not-allowed',
          className,
        )}
      >
        {asChild ? (
          children
        ) : (
          <>
            {icon ? <span className="inline-flex shrink-0 items-center">{icon}</span> : null}
            {children ? (
              <span
                className={tw('inline-flex items-center', size === 'small' ? 'px-0.5' : 'px-1')}
              >
                {children}
              </span>
            ) : null}
          </>
        )}
      </Comp>
    </TabsPrimitive.Trigger>
  )
}

/** The view a tab selects. Renders only while its tab is selected. */
const SectionTabsPanel = TabsPrimitive.Content

/**
 * SectionTabs toggle between views within a page section. For top-level page navigation, use
 * `NavigationTabs`.
 *
 * `Root` owns the selection and the accessible wiring, so a tab is never just a styled button:
 * `List` is the `tablist`, each `Tab` is a `tab` carrying `aria-selected` and arrow-key focus, and
 * each `Panel` is the `tabpanel` its tab labels. Give a `Tab` and its `Panel` the same `value` —
 * there is no `data-state` or `onClick` to manage, and a `Tab` outside a `Root` will not compile.
 *
 * ```tsx
 * <SectionTabs.Root value={tab} onValueChange={setTab}>
 *   <SectionTabs.List>
 *     <SectionTabs.Tab value="templates">Templates</SectionTabs.Tab>
 *     <SectionTabs.Tab value="community">Community</SectionTabs.Tab>
 *   </SectionTabs.List>
 *   <SectionTabs.Panel value="templates">…</SectionTabs.Panel>
 *   <SectionTabs.Panel value="community">…</SectionTabs.Panel>
 * </SectionTabs.Root>
 * ```
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=2212-23215
 */
export const SectionTabs = {
  Root: SectionTabsRoot,
  List: SectionTabsList,
  Tab: SectionTabsTab,
  Panel: SectionTabsPanel,
}

const sectionTabsClasses = {
  base: [
    'inline-flex shrink-0 items-center whitespace-nowrap outline-none transition-colors',
    'rounded-studio-geometry-semantic-radius-sm',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    focusRing,
  ].join(' '),
  sizes: {
    large: 'h-10 px-4 py-2 studio-typography-14 [&_svg]:size-6',
    medium: 'h-8 px-2 py-1 studio-typography-14 [&_svg]:size-6',
    small: 'h-7 p-1 studio-typography-12 [&_svg]:size-5',
  } satisfies Record<SectionTabsSize, string>,
  variants: {
    regular: [
      'backdrop-blur',
      'bg-theme_semantic_action_select_primary_background-color_default',
      'text-theme_semantic_action_select_primary_foreground-color_default',
      'enabled:[&:hover:not(:focus-visible):not([data-state=active])]:bg-theme_semantic_action_select_primary_background-color_hover',
      'enabled:[&:active:not([data-state=active])]:bg-theme_semantic_action_select_primary_background-color_active',
      '[&:focus-visible:not([data-state=active])]:bg-theme_semantic_action_select_primary_background-color_focus',
      'data-[state=active]:bg-theme_semantic_action_select_primary_background-color_selected',
      'data-[state=active]:text-theme_semantic_action_select_primary_foreground-color_selected',
      'disabled:bg-theme_semantic_action_select_primary_background-color_disabled',
      'disabled:text-theme_semantic_action_select_primary_foreground-color_disabled',
    ].join(' '),
    subtle: [
      'text-theme_semantic_action_select_tertiary_text-color-default',
      'enabled:[&:hover:not(:focus-visible):not([data-state=active])]:bg-theme_semantic_action_select_tertiary_background-color-hover',
      'enabled:[&:hover:not(:focus-visible):not([data-state=active])]:text-theme_semantic_action_select_tertiary_text-color-hover',
      'enabled:[&:active:not([data-state=active])]:bg-theme_semantic_action_select_tertiary_background-color-active',
      'enabled:[&:active:not([data-state=active])]:text-theme_semantic_action_select_tertiary_text-color-active',
      '[&:focus-visible:not([data-state=active])]:bg-theme_semantic_action_select_tertiary_background-color-focus',
      '[&:focus-visible:not([data-state=active])]:text-theme_semantic_action_select_tertiary_text-color-focus',
      'data-[state=active]:bg-theme_semantic_action_select_tertiary_background-color-selected',
      'data-[state=active]:text-theme_semantic_action_select_tertiary_text-color-selected',
      'disabled:text-theme_semantic_action_select_tertiary_text-color-disabled',
    ].join(' '),
  } satisfies Record<SectionTabsVariant, string>,
}
